/**
 * Product adapter
 * Converts Storefront API Product to Analyzify format
 */

import type { StorefrontProduct, StorefrontVariant } from '../types/storefront';
import type { AnalyzifyProduct, AnalyzifyVariant, AnalyzifyProductData } from '../types/products';
import { extractNumericId } from './ids';

/**
 * Convert money string to cents
 * "19.99" -> 1999
 * Defensively handles null/undefined/NaN amounts
 */
function moneyToCents(amount: string | null | undefined): number {
  if (!amount) return 0;
  const parsed = parseFloat(amount);
  return isNaN(parsed) ? 0 : Math.round(parsed * 100);
}

/**
 * Adapt a Storefront API variant to Analyzify format
 * Defensively handles null/undefined variant data
 */
export function adaptVariant(variant: StorefrontVariant | null | undefined): AnalyzifyVariant {
  // Return safe default for null/undefined variant
  if (!variant) {
    return {
      id: 0,
      title: '',
      price: 0,
      available: false,
      sku: '',
      barcode: null,
      compare_at_price: null,
      public_title: '',
      option1: null,
      option2: null,
      option3: null,
    };
  }

  const title = variant.title || '';

  // Extract option values from selectedOptions array
  // CDN scripts expect option1, option2, option3 fields for variantChange event
  const selectedOptions = variant.selectedOptions || [];
  const option1 = selectedOptions[0]?.value || null;
  const option2 = selectedOptions[1]?.value || null;
  const option3 = selectedOptions[2]?.value || null;

  return {
    id: extractNumericId(variant.id),
    title,
    price: moneyToCents(variant.price?.amount),
    available: variant.availableForSale ?? false,
    sku: variant.sku || '',
    barcode: variant.barcode || null,
    compare_at_price: variant.compareAtPrice?.amount
      ? moneyToCents(variant.compareAtPrice.amount)
      : null,
    // public_title is used as fallback in extension
    public_title: title !== 'Default Title' ? title : '',
    option1,
    option2,
    option3,
  };
}

/**
 * Create empty/default product data for null inputs
 */
function createEmptyProductData(): AnalyzifyProductData {
  const emptyVariant = adaptVariant(null);
  return {
    product: {
      id: 0,
      title: '',
      handle: '',
      type: '',
      vendor: '',
      price: 0,
      compare_at_price: null,
      available: false,
      tags: [],
      options: [],
      variants: [emptyVariant],
      featured_image: undefined,
    },
    variant: emptyVariant,
    collection: undefined,
  };
}

/**
 * Adapt a Storefront API product to Analyzify format
 * Defensively handles null/undefined product data
 */
export function adaptProduct(
  product: StorefrontProduct | null | undefined,
  selectedVariantId?: string
): AnalyzifyProductData {
  // Return safe default for null/undefined product
  if (!product) {
    return createEmptyProductData();
  }

  // Safely get variants from edges - guard against null/empty
  const variantEdges = product.variants?.edges || [];
  const variants = variantEdges.map((edge) => adaptVariant(edge?.node));

  // Ensure at least one variant exists
  if (variants.length === 0) {
    variants.push(adaptVariant(null));
  }

  // Extract unique option names from first variant's selectedOptions
  // This gives us the product's option names (e.g., ["Color", "Size"])
  const options: string[] = [];
  const firstVariantOptions = variantEdges[0]?.node?.selectedOptions;
  if (firstVariantOptions && Array.isArray(firstVariantOptions)) {
    firstVariantOptions.forEach((opt) => {
      if (opt?.name && !options.includes(opt.name)) {
        options.push(opt.name);
      }
    });
  }

  // Find selected variant or use first
  let selectedVariant = variants[0];
  if (selectedVariantId && variantEdges.length > 0) {
    const found = variantEdges.find(
      (edge) =>
        edge?.node?.id === selectedVariantId ||
        extractNumericId(edge?.node?.id).toString() === selectedVariantId
    );
    if (found?.node) {
      selectedVariant = adaptVariant(found.node);
    }
  }

  // Build Analyzify product with defensive null checks
  const analyzifyProduct: AnalyzifyProduct = {
    id: extractNumericId(product.id),
    title: product.title || '',
    handle: product.handle || '',
    type: product.productType || '',
    vendor: product.vendor || '',
    price: moneyToCents(product.priceRange?.minVariantPrice?.amount),
    compare_at_price: product.compareAtPriceRange?.minVariantPrice?.amount
      ? moneyToCents(product.compareAtPriceRange.minVariantPrice.amount)
      : null,
    available: product.availableForSale ?? false,
    tags: Array.isArray(product.tags) ? product.tags : [],
    options: options,
    variants: variants,
    featured_image: product.featuredImage?.url,
  };

  // Build taxonomy info if available (Google Product Category)
  let taxonomy: AnalyzifyProductData['taxonomy'];
  if (product.productCategory?.productTaxonomyNode) {
    taxonomy = {
      id: product.productCategory.productTaxonomyNode.id || '',
      name: product.productCategory.productTaxonomyNode.name || '',
      ancestors: product.productCategory.ancestorIds || [],
    };
  }

  // Build collection info if available
  let collection: AnalyzifyProductData['collection'];
  const firstCollection = product.collections?.edges?.[0]?.node;
  if (firstCollection) {
    collection = {
      id: extractNumericId(firstCollection.id),
      title: firstCollection.title || '',
      handle: firstCollection.handle || '',
    };
  }

  return {
    product: analyzifyProduct,
    taxonomy,
    variant: selectedVariant,
    collection,
  };
}

/**
 * Adapt multiple products for list views
 * Defensively handles null/undefined array
 */
export function adaptProductList(
  products: StorefrontProduct[] | null | undefined
): AnalyzifyProduct[] {
  if (!products || !Array.isArray(products)) {
    return [];
  }
  return products.map((product) => adaptProduct(product).product);
}
